"use client";

import { toast } from "@/app/_providers/toast";
import { Button } from "@components/core/Button";
import { cn } from "@ui/lib/utils";
import { ArrowUpIcon, LinkIcon } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";

import { useSTTActions, useSTTSelectedTabData } from "./STTStore";
import { useSTTTranslations } from "./STTTranslations";
import { MEDIA_CONTENT_TYPES } from "./utils";

const MAX_UPLOAD_SIZE_MB = 100;
const BYTES_PER_MB = 1024 * 1024;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * BYTES_PER_MB;

export function STTfromURL({
  title,
  description,
  placeholder,
  cta,
}: {
  cta?: null | string;
  description?: null | string;
  placeholder?: null | string;
  title?: null | string;
}) {
  const t = useSTTTranslations();
  const { globalTranscribeFile, createNewTabFromUrlTranscription } = useSTTActions();
  const selectedTabData = useSTTSelectedTabData();
  const [url, setUrl] = useState("");

  const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    accept: Object.fromEntries(MEDIA_CONTENT_TYPES.map(type => [type, []])),
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    onDropAccepted: files => {
      const file = files[0];
      globalTranscribeFile(file);
    },
    onDropRejected: rejected => {
      const { file, errors } = rejected[0];
      let errorMessage = `"${file.name}" could not be processed. Please try another file.`;

      if (errors.some(e => e.code === "file-invalid-type")) {
        errorMessage = `"${file.name}" is not supported. Please choose an audio or video file.`;
      } else if (errors.some(e => e.code === "file-too-large")) {
        errorMessage = `"${file.name}" exceeds the ${MAX_UPLOAD_SIZE_MB}MB size limit.`;
      }

      toast.error(errorMessage);
    },
  });

  const handleUrlSubmit = () => {
    try {
      const urlVal = new URL(url.trim());
      createNewTabFromUrlTranscription(urlVal.toString(), {
        icon: selectedTabData?.icon || "",
      });
    } catch (error) {
      toast.error("Please enter a valid URL");
    }
  };

  return (
    <div className={cn("relative flex flex-1 flex-col overflow-hidden rounded-inxl bg-default shadow-xs")}>
      {/* URL Input Section */}
      <div className='flex flex-1 items-center justify-center pb-24 pt-28'>
        <div className='mx-auto max-w-3xl space-y-16 px-12 text-center'>
          <div className='mx-auto space-y-8 md:w-2/3'>
            <h3 className='f-subhead-01 font-semibold opacity-80'>{title || t("url_widget.title")}</h3>
            <p className='f-body-01 text-light'>{description || t("url_widget.description")}</p>
          </div>

          <div className='space-y-12'>
            <div className='relative'>
              <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-12'>
                <LinkIcon className='h-16 w-16 text-default' />
              </div>
              <input
                className='w-full rounded-lg border border-default px-12 py-10 pl-44 text-default placeholder:text-lighter focus:border-dark focus:outline-none focus:ring-1 focus:ring-dark'
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleUrlSubmit();
                  }
                }}
                placeholder={placeholder || t("url_widget.placeholder")}
                type='url'
                value={url}
              />
            </div>

            <Button className='w-full' disabled={!url.trim()} onClick={handleUrlSubmit} size='lg' variant='primary'>
              {cta || t("url_widget.cta")}
            </Button>
          </div>
        </div>
      </div>

      {/* OR Divider */}
      <div className='relative hidden h-1 lg:block'>
        <div className='absolute inset-0 items-center'>
          <div className='border-light w-full border-t' />
        </div>
        <div className='absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center'>
          <span className='f-ui-01 rounded-lg bg-default px-12 text-lg text-light'>{t("url_widget.or")}</span>
        </div>
      </div>

      {/* Upload Section */}
      <div className='hidden items-center justify-center lg:flex'>
        <div
          {...getRootProps()}
          className={cn(
            "flex h-full w-full cursor-pointer flex-col items-center justify-center space-y-12 p-24 transition-colors hover:bg-[#F9FAFC]",
            isDragActive && "bg-[#F9FAFC]",
          )}
        >
          <input {...getInputProps()} />

          <div className='flex flex-col items-center space-y-12'>
            <div className='border-light bg-white/10 rounded-lg border p-8 text-light'>
              <ArrowUpIcon className='h-20 w-20 text-default' />
            </div>

            <div className='space-y-4 text-center'>
              <h3 className='f-subhead-01 font-semibold opacity-80'>{t("url_widget.upload_section.title")}</h3>
              <p className='f-body-01 max-w-sm text-light'>{t("url_widget.upload_section.description")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
